/* eslint-disable no-underscore-dangle */
import { App } from '../hooks/use-app';
import { loadColumns } from '../hooks/use-form-app';
import { chunkLinkedFromRecordTitles, linkedToRecordTitles } from './sql-fragments';

export interface FormLinkItem {
  // eslint-disable-next-line camelcase
  form_id: string
  key?: string
}

export interface FormLink {
  to?: FormLinkItem[];
  from?: FormLinkItem[];
}

export interface LinkDataMap {
  recordIds: string[];
  children: LinkChildren;
}

interface LinkedChild {
  [key: string]: {
    records: string[]
  }
}

interface LinkChildren {
  [key: string]: { // {'child-form-id': {...}}
    childRecords: LinkedChild
    recordIds: string[]
  }
}

interface RecordObject {
  formId: string;
  childRecordIds: string[];
}

interface SchemaColumn {
  name: string;
  field?: string | null;
}

export interface FormSchemas {
  [key: string]: {
    form: SchemaColumn[]
    repeatables?: { [key: string]: SchemaColumn[] }
  }
}

// Rails `update_form_links` uses `all_of_type('RecordLinkField')` which recurses
// into Repeatables, so form_links.from entries exist for RecordLinkFields at any
// nesting depth. But `loadColumns` splits columns by table — top-level fields land
// in schema.form while fields inside Repeatables land in schema.repeatables[key].
// We must search both to resolve the column name for the SQL query.
function findFieldInSchema(
  schema: FormSchemas[string], fieldKey: string,
): { name: string; repeatableKey?: string } | null {
  const formField = schema.form.find((col) => col.field === fieldKey);
  if (formField?.name) {
    return { name: formField.name };
  }

  if (schema.repeatables) {
    for (const [repeatableKey, columns] of Object.entries(schema.repeatables)) {
      const repeatableField = columns.find((col) => col.field === fieldKey);
      if (repeatableField?.name) {
        return { name: repeatableField.name, repeatableKey };
      }
    }
  }

  return null;
}

export async function fetchParentSchemas(formLinks: FormLink, app: App): Promise<FormSchemas> {
  const response = {};
  if (formLinks.from) {
    const ids: string[] = formLinks.from.map((fromLink) => fromLink.form_id);
    const schemas = await Promise.all(ids.map((id) => loadColumns(app.dataSource, id)));
    ids.forEach((id, index) => {
      const schema = schemas[index];
      if (schema.form.length) {
        response[id] = schema;
      }
    });
  }
  return response;
}

export async function fetchParentLinkedRecords(
  linkDataMap: LinkDataMap, schemas: FormSchemas, formLinks: FormLink, app: App,
) {
  const parentFormIds = Object.keys(schemas);
  const allRecordIds = linkDataMap.recordIds;

  const sqlStatementMap = new Map<string, string[]>();
  parentFormIds.forEach((parentFormId) => {
    const fromLink = formLinks.from?.find((link) => (link.form_id === parentFormId));
    const schema = schemas[parentFormId];
    const fieldMatch = findFieldInSchema(schema, fromLink?.key || '');

    if (!fieldMatch) {
      return;
    }

    sqlStatementMap.set(
      parentFormId,
      chunkLinkedFromRecordTitles(parentFormId, fieldMatch.name, allRecordIds, fieldMatch.repeatableKey),
    );
  });

  const response = {};
  if (allRecordIds.length) {
    const parents: any[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const parentFormId of parentFormIds) {
      const statements = sqlStatementMap.get(parentFormId) || [];
      // this avoids errors produced by a too large query request
      // this is a temporary solution until we can use a more efficient query
      // eslint-disable-next-line no-await-in-loop
      const result = await Promise.all(statements.map((q) => app.api.query.queryRecords({ q })));
      parents.push(result.flat());
    }

    parentFormIds.forEach((parentFormId, index) => {
      const parentRecords = parents[index];
      parentRecords.forEach((parent: any) => {
        // eslint-disable-next-line no-underscore-dangle
        parent._child_record_id.forEach((recordId: any) => {
          if (!response[recordId]) {
            response[recordId] = [];
          }
          response[recordId].push({
            formId: parentFormId, recordId: parent._parent_record_id, title: parent._title,
          });
        });
      });
    });
  }
  return response;
}

export async function fetchChildLinkedRecords(linkDataMap: LinkDataMap, app: App) {
  const formIds = Object.keys(linkDataMap.children);
  const sqlStatements = formIds.map((formId) => {
    const children = linkDataMap.children[formId];
    const childIds = Object.keys(children.childRecords);
    return linkedToRecordTitles(formId, childIds);
  });

  const childRecords = await Promise
    .all(sqlStatements.map((q) => app.api.query.queryRecords({ q })));
  const response = {};

  formIds.forEach((formId, index) => {
    const formRecords = linkDataMap.children[formId];
    const records = formRecords.childRecords;
    const children = childRecords[index];
    children.forEach((child: any) => {
      // eslint-disable-next-line no-underscore-dangle
      const childRecId = child?._record_id;
      const linkedToChildRecord = records[childRecId];
      linkedToChildRecord.records.forEach((recId) => {
        if (!response[recId]) {
          response[recId] = [];
        }
        // eslint-disable-next-line no-underscore-dangle
        response[recId].push({ formId, recordId: childRecId, title: child?._title });
      });
    });
  });
  return response;
}

function createRecordDataArray(record: Record<string, any>, formLinks: FormLink): RecordObject[] {
  const recordObjects: RecordObject[] = [];
  if (formLinks.to) {
    const toLinks = formLinks.to;
    toLinks.forEach((toLink) => {
      const formValues = record.form_values;
      const toRecords: any = formValues[toLink?.key || ''];

      if (toRecords) {
        const childRecordIds: string[] = toRecords.map((rec: any) => rec.record_id);
        recordObjects.push({ formId: toLink.form_id, childRecordIds });
      }
    });
  }

  return recordObjects;
}

function addChildren(
  children: LinkChildren, recordId: string, recordObjects: RecordObject[],
): void {
  if (recordObjects.length) {
    recordObjects.forEach((recordObject) => {
      const { formId } = recordObject;
      if (!children[formId]) {
        // eslint-disable-next-line no-param-reassign
        children[formId] = { childRecords: {}, recordIds: [] };
      }

      recordObject.childRecordIds.forEach((childId: string) => {
        const entry = children[formId];
        if (!entry.childRecords[childId]) {
          entry.childRecords[childId] = { records: [] };
        }
        entry.childRecords[childId].records.push(recordId);
        if (children[formId].recordIds.indexOf(recordId) < 0) {
          children[formId].recordIds.push(recordId);
        }
      });
    });
  }
}

export function createLinkDataMap(
  records: Record<string, any>, formLinks: FormLink,
): LinkDataMap {
  const children = {};
  const ids: string[] = records.map((rec: Record<string, any>) => {
    const recordObjects = createRecordDataArray(rec, formLinks);
    addChildren(children, rec.id, recordObjects);
    return rec.id;
  });
  const recordIds: string[] = [...new Set(ids)];

  return { recordIds, children };
}
